import { Logger, withPrefix } from '@gitlab-org/logging';
import { Injectable, createInterfaceId } from '@gitlab-org/di';
import { WorkflowGraphQLService as WorkflowServiceInterface } from '@gitlab-org/webview-duo-workflow/src/plugin';
import {
  DuoWorkflowEventPayload,
  DuoWorkflowStatus,
} from '@gitlab-org/webview-duo-workflow/src/types';
import { GitLabApiClient } from '../../api';
import {
  DuoWorkflowCheckpoint,
  DuoWorkflowEventConnectionType,
  DuoWorkflowInfo,
  LangGraphPayload,
  ProjectInfo,
  ProjectsPayload,
  WorkflowsPayload,
} from './types';
import { GET_PROJECTS_QUERY, GET_WORKFLOW_EVENTS_QUERY, GET_USER_WORKFLOWS } from './queries';

const WorkflowService = createInterfaceId<WorkflowServiceInterface>('WorkflowService');
const LoggerInterface = createInterfaceId<Logger>('Logger');

@Injectable(WorkflowService, [GitLabApiClient, LoggerInterface])
export class WorkflowGraphQLService implements WorkflowServiceInterface {
  #apiClient: GitLabApiClient;

  #logger: Logger;

  constructor(apiClient: GitLabApiClient, logger: Logger) {
    this.#apiClient = apiClient;
    this.#logger = withPrefix(logger, '[WorkflowGraphQLService]');
  }

  generateWorkflowID(id: string): string {
    return `gid://gitlab/Ai::DuoWorkflows::Workflow/${id}`;
  }

  parseLangGraphCheckpoint(langGraphCheckpoint: string): DuoWorkflowCheckpoint {
    try {
      return JSON.parse(langGraphCheckpoint);
    } catch (e) {
      const error = e as Error;
      this.#logger.error(error);
      throw new Error('Failed to parse checkpoint');
    }
  }

  getLatestCheckpoint(duoWorkflowEvents: DuoWorkflowEventPayload[]): DuoWorkflowEventPayload {
    if (!duoWorkflowEvents.length) {
      return {
        metadata: '',
        errors: [],
        checkpoint: {
          ts: new Date().toISOString(),
          channel_values: {
            status: 'Not Started',
          },
        },
      };
    }

    const sortedCheckpointes = [...duoWorkflowEvents].sort(
      (a: DuoWorkflowEventPayload, b: DuoWorkflowEventPayload) => {
        return new Date(b.checkpoint.ts).getTime() - new Date(a.checkpoint.ts).getTime();
      },
    );

    return sortedCheckpointes[0];
  }

  getStatus(duoWorkflowEvent: DuoWorkflowEventPayload): DuoWorkflowStatus {
    return duoWorkflowEvent.checkpoint.channel_values.status;
  }

  async getWorkflowById(id: string): Promise<DuoWorkflowEventPayload> {
    try {
      const data: DuoWorkflowEventConnectionType = await this.#apiClient.fetchFromApi({
        type: 'graphql',
        query: GET_WORKFLOW_EVENTS_QUERY,
        variables: { workflowId: id },
      });

      const duoWorkflowEvents: DuoWorkflowEventPayload[] = data?.duoWorkflowEvents?.nodes?.map(
        (node: LangGraphPayload) => {
          return { ...node, checkpoint: this.parseLangGraphCheckpoint(node.checkpoint) };
        },
      );
      // TODO: When we have a guarantee that events are sorted, we can drop this and
      // instead ask only for one event in the query
      return this.getLatestCheckpoint(duoWorkflowEvents) || [];
    } catch (e) {
      const error = e as Error;
      this.#logger.error(error.message);
      throw new Error(error.message);
    }
  }

  async getUserWorkflows(): Promise<DuoWorkflowInfo[]> {
    try {
      const data: WorkflowsPayload = await this.#apiClient.fetchFromApi({
        type: 'graphql',
        query: GET_USER_WORKFLOWS,
        variables: {},
      });

      return data?.duoWorkflowWorkflows?.edges?.map((edge) => edge.node) || [];
    } catch (e) {
      const error = e as Error;
      this.#logger.error(error.message);
      throw new Error(error.message);
    }
  }

  async getProjects(projectIds: string[]): Promise<ProjectInfo[]> {
    try {
      const data: ProjectsPayload = await this.#apiClient.fetchFromApi({
        type: 'graphql',
        query: GET_PROJECTS_QUERY,
        variables: { ids: projectIds },
      });

      return data?.projects?.nodes;
    } catch (e) {
      const error = e as Error;
      this.#logger.error(error.message);
      throw new Error(error.message);
    }
  }
}
